import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  untracked
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {
  IconBoldDocumentDownloadComponent
} from '@components/icons/icon-bold-document-download/icon-bold-document-download.component';
import {IconBoldTrash} from '@components/icons/icon-bold-trash/icon-bold-trash';
import {IconCloseSquareComponent} from '@components/icons/icon-close-square/icon-close-square.component';
import {IconImageBoldComponent} from '@components/icons/icon-image-bold/icon-image-bold.component';
import {Anexo} from '@core/models/anexo';
import {TIPO_ARQUIVO_FOTOS_ID, TipoAnexo} from '@core/models/tipo-anexo';
import {AuthService} from '@core/services/auth.service';
import {DemandaAnexosService} from '@core/services/demanda-anexos.service';
import {StringUtils} from '@shared/utils/string.utils';
import {MessageService, PrimeTemplate} from 'primeng/api';
import {Button} from 'primeng/button';
import {FileSelectEvent, FileUpload} from 'primeng/fileupload';
import {InputText} from 'primeng/inputtext';
import {Select} from 'primeng/select';
import {TableModule} from 'primeng/table';
import {Tooltip} from 'primeng/tooltip';
import {
  IconBoldDocumentUploadComponent
} from '@components/icons/icon-bold-document-upload/icon-bold-document-upload.component';

interface AnexoUploadItem {
  id: number;
  file: File;
  idTipoAnexo: number;
  status: 'uploading' | 'done' | 'error';
  anexo?: Anexo;
  descricaoOutros?: string | null;
  urlImagem?: string;
}

interface FotoView extends Anexo {
  status: 'loading' | 'loaded' | 'error';
  urlImagem?: string;
}

@Component({
  selector: 'app-form-anexos-demanda',
  imports: [
    ReactiveFormsModule, Select, FileUpload, TableModule,
    InputText, PrimeTemplate, Tooltip,
    IconBoldDocumentDownloadComponent, IconBoldTrash, Button, IconCloseSquareComponent, IconImageBoldComponent, IconBoldDocumentUploadComponent,
  ],
  templateUrl: './form-anexos-demanda.component.html',
  styleUrl: './form-anexos-demanda.component.css',
})
export class FormAnexosDemandaComponent implements OnInit, OnDestroy {
  private static nextId = 0;
  protected readonly fieldId = `form-anexos-${++FormAnexosDemandaComponent.nextId}`;
  readonly editMode = input<boolean>(true);
  readonly viewAnexos = input<Anexo[]>();
  readonly initialAnexos = input<Anexo[]>([]);
  readonly anexosChange = output<Anexo[]>();
  readonly uploadStatusChange = output<boolean>();

  private readonly fb = inject(FormBuilder);
  private readonly anexosService = inject(DemandaAnexosService);
  private readonly messageService = inject(MessageService);
  private readonly authService = inject(AuthService);

  protected usuarioAutenticado = computed(() => this.authService.isAuthenticated());
  protected readonly iconSize = signal(window.innerWidth <= 1280 ? '18' : '24');
  protected readonly form: FormGroup = this.fb.group({
    tipoDocumento: [{value: null as TipoAnexo | null, disabled: !this.editMode()}],
    descricaoArquivo: [{value: '', disabled: !this.editMode()}],
  });

  @HostListener('window:resize')
  onResize(): void {
    this.iconSize.set(window.innerWidth <= 1280 ? '18' : '24');
  }

  private readonly tipoDocumentoSignal = toSignal(
    this.form.get('tipoDocumento')!.valueChanges,
    {initialValue: null as TipoAnexo | null}
  );

  private readonly FOTO_MAX_BYTES = 10 * 1024 * 1024;
  private readonly FOTO_MAX_COUNT = 5;
  protected readonly FOTO_EXTENSIONS = ['jpg', 'jpeg', 'png', 'bmp'];
  private readonly DOC_MAX_BYTES = 15 * 1024 * 1024;
  private readonly DOC_EXTENSIONS = ['pdf', 'xlsx'];

  protected fotoFileTooLarge = false;
  protected fotoFileInvalidExtension = false;
  protected docFileTooLarge = false;
  protected docFileInvalidExtension = false;

  protected readonly tiposAnexos = signal<TipoAnexo[]>([]);
  protected readonly anexosEmAndamento = signal<AnexoUploadItem[]>([]);
  private anexoCounter = 0;
  private readonly objectUrlCache = new Map<File, string>();

  protected readonly tipoFoto = computed(() =>
    this.tiposAnexos().find(t => t.id === TIPO_ARQUIVO_FOTOS_ID)
  );
  protected readonly tiposDocumento = computed(() =>
    this.tiposAnexos().filter(t => t.id !== TIPO_ARQUIVO_FOTOS_ID)
  );
  protected readonly fotosEnviadas = computed(() =>
    this.anexosEmAndamento().filter(u => u.idTipoAnexo === TIPO_ARQUIVO_FOTOS_ID && u.status !== 'error')
  );
  protected readonly fotosAtingiramLimite = computed(() =>
    this.fotosEnviadas().length >= (this.tipoFoto()?.quantidadeMaxima ?? this.FOTO_MAX_COUNT)
  );
  protected readonly documentosEnviados = computed(() =>
    this.anexosEmAndamento().filter(u => u.idTipoAnexo !== TIPO_ARQUIVO_FOTOS_ID && u.status !== 'error')
  );
  protected readonly uploadEmAndamento = computed(() =>
    this.anexosEmAndamento().some(u => u.status === 'uploading')
  );
  protected readonly fotosView = signal<FotoView[]>([]);
  protected readonly documentosView = computed(() =>
    this.viewAnexos()?.filter(a => a.idTipoAnexo !== TIPO_ARQUIVO_FOTOS_ID) ?? []
  );
  protected readonly tipoDocumentoSelecionadoAtingiuLimite = computed(() => {
    const tipo = this.tipoDocumentoSignal();
    if (!tipo) return false;
    const enviados = this.anexosEmAndamento().filter(u => u.idTipoAnexo === tipo.id && u.status !== 'error').length;
    return enviados >= tipo.quantidadeMaxima;
  });

  constructor() {
    effect(() => {
      this.uploadStatusChange.emit(this.uploadEmAndamento());
    });
    effect(() => {
      const fotos = (this.viewAnexos() ?? []).filter(a => a.idTipoAnexo === TIPO_ARQUIVO_FOTOS_ID);
      for (const foto of untracked(() => this.fotosView())) {
        if (foto.urlImagem && foto.urlImagem !== foto.urlLocal) URL.revokeObjectURL(foto.urlImagem);
      }
      this.fotosView.set(fotos.map(a => ({
        ...a,
        status: a.urlLocal ? 'loaded' as const : 'loading' as const,
        urlImagem: a.urlLocal,
      })));
      for (const foto of fotos) {
        if (foto.urlLocal) continue;
        if (!this.usuarioAutenticado()) continue;
        this.anexosService.download(foto.idArquivo).subscribe({
          next: (response) => {
            if (!response.body) return;
            const urlImagem = URL.createObjectURL(response.body);
            this.atualizarFoto(foto.idArquivo, {status: 'loaded', urlImagem});
          },
          error: () => {
            this.atualizarFoto(foto.idArquivo, {status: 'error'});
          }
        });
      }
    });
  }

  private atualizarFoto(idArquivo: number, patch: Partial<FotoView>): void {
    this.fotosView.update(list => list.map(f => f.idArquivo === idArquivo ? {...f, ...patch} : f));
  }

  ngOnDestroy(): void {
    for (const foto of this.fotosView()) {
      if (foto.urlImagem && foto.urlImagem !== foto.urlLocal) URL.revokeObjectURL(foto.urlImagem);
    }
  }

  ngOnInit(): void {
    this.subscribeTipoDocumentoValueChanges();
    this.getTiposAnexos();
    this.restoreInitialAnexos();
  }

  private restoreInitialAnexos(): void {
    const initial = this.initialAnexos();
    if (!initial.length) return;
    const restored: AnexoUploadItem[] = initial.map(anexo => ({
      id: ++this.anexoCounter,
      file: new File([], anexo.nomeOriginal),
      idTipoAnexo: anexo.idTipoAnexo,
      status: 'done' as const,
      anexo,
      descricaoOutros: anexo.descricaoOutros,
      urlImagem: anexo.urlLocal,
    }));
    this.anexosEmAndamento.set(restored);
    this.emitAnexos();
  }

  protected get tipoDocumentoSelecionado(): TipoAnexo | null {
    return this.form.get('tipoDocumento')?.value ?? null;
  }

  protected getFotoFormattedMaxSize(): string {
    return StringUtils.getFormattedFileSize(this.tipoFoto()?.tamanhoMaxBytes ?? this.FOTO_MAX_BYTES);
  }

  protected getDocFormattedMaxSize(): string {
    return StringUtils.getFormattedFileSize(this.tipoDocumentoSelecionado?.tamanhoMaxBytes ?? this.DOC_MAX_BYTES);
  }

  protected getDocFormattedExtensions(): string {
    return (this.tipoDocumentoSelecionado?.extensoes ?? this.DOC_EXTENSIONS).map(e => e.toUpperCase()).join(' ou ');
  }

  protected getTipoNome(idTipoAnexo: number): string {
    return this.tiposAnexos().find(t => t.id === idTipoAnexo)?.nome ?? '';
  }

  protected getTipoDocumentoLabel(item: {
    idTipoAnexo: number;
    descricaoOutros?: string | null;
  }): string {
    const tipoNome = this.getTipoNome(item.idTipoAnexo);
    if (tipoNome === 'Outros' && item.descricaoOutros) {
      return `Outros - ${item.descricaoOutros}`;
    }
    return tipoNome || '-';
  }

  protected onSelectFoto(event: FileSelectEvent): void {
    this.fotoFileTooLarge = false;
    this.fotoFileInvalidExtension = false;
    const file = event.currentFiles[0];
    if (!file) return;
    const extensoes = this.tipoFoto()?.extensoes ?? this.FOTO_EXTENSIONS;
    const maxBytes = this.tipoFoto()?.tamanhoMaxBytes ?? this.FOTO_MAX_BYTES;
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!extensoes.includes(ext)) {
      this.fotoFileInvalidExtension = true;
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção!',
        detail: `Formato inválido. Formatos permitidos: ${extensoes.map(e => e.toUpperCase()).join(', ')}`
      });
      return;
    }
    if (file.size > maxBytes) {
      this.fotoFileTooLarge = true;
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção!',
        detail: `Tamanho do arquivo não permitido. Somente arquivos até ${this.getFotoFormattedMaxSize()}.`
      });
      return;
    }
    if (this.fotosAtingiramLimite()) return;
    this.upload(file, TIPO_ARQUIVO_FOTOS_ID);
  }

  protected onSelectDocumento(event: FileSelectEvent): void {
    this.docFileTooLarge = false;
    this.docFileInvalidExtension = false;
    const file = event.currentFiles[0];
    const tipoDoc = this.tipoDocumentoSelecionado;
    if (!file || !tipoDoc) return;
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const extensoes = tipoDoc.extensoes ?? this.DOC_EXTENSIONS;
    if (!extensoes.includes(ext)) {
      this.docFileInvalidExtension = true;
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção!',
        detail: `Formato inválido. Formatos permitidos: ${extensoes.map(e => e.toUpperCase()).join(', ')}`
      });
      return;
    }
    if (file.size > (tipoDoc.tamanhoMaxBytes ?? this.DOC_MAX_BYTES)) {
      this.docFileTooLarge = true;
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção!',
        detail: `Tamanho do arquivo excedido. Máx: ${this.getDocFormattedMaxSize()}.`
      });
      return;
    }
    this.upload(file, tipoDoc.id);
  }

  protected onRemoverFoto(id: number): void {
    const item = this.anexosEmAndamento().find(u => u.id === id);
    if (!item) return;
    if (item.anexo) {
      if (!this.usuarioAutenticado()) return;
      this.anexosService.deleteTemp(item.anexo.nomeUnicoIdentificador).subscribe({
        next: () => {
          this.revokeObjectUrl(item.file);
          this.removeItem(id);
          this.messageService.add({severity: 'success', summary: 'Sucesso!', detail: 'Anexo excluído com sucesso.'});
        },
        error: () => this.messageService.add({severity: 'error', summary: 'Erro!', detail: 'Falha ao remover arquivo.'})
      });
    } else {
      this.revokeObjectUrl(item.file);
      this.removeItem(id);
    }
  }

  protected onRemoverDocumento(id: number): void {
    const item = this.anexosEmAndamento().find(u => u.id === id);
    if (item?.anexo) {
      if (!this.usuarioAutenticado()) return;
      this.anexosService.deleteTemp(item.anexo.nomeUnicoIdentificador).subscribe({
        next: () => {
          this.removeItem(id);
          this.messageService.add({severity: 'success', summary: 'Sucesso!', detail: 'Anexo excluído com sucesso.'});
        },
        error: () => this.messageService.add({severity: 'error', summary: 'Erro!', detail: 'Falha ao remover arquivo.'})
      });
    } else {
      this.removeItem(id);
    }
  }

  protected onDownloadDocumento(item: AnexoUploadItem): void {
    try {
      const url = URL.createObjectURL(item.file);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.file.name;
      a.click();
      URL.revokeObjectURL(url);
      this.messageService.add({severity: 'success', summary: 'Sucesso!', detail: 'Download realizado com sucesso.'});
    } catch {
      this.messageService.add({severity: 'error', summary: 'Erro!', detail: 'Falha ao realizar o download.'});
    }
  }

  protected onDownloadAnexo(anexo: Anexo): void {
    if (anexo.urlLocal) {
      const a = document.createElement('a');
      a.href = anexo.urlLocal;
      a.download = anexo.nomeOriginal;
      a.click();
      this.messageService.add({severity: 'success', summary: 'Sucesso!', detail: 'Download realizado com sucesso.'});
      return;
    }
    if (!this.usuarioAutenticado()) return;
    this.anexosService.download(anexo.idArquivo).subscribe({
      next: (response) => {
        const contentDisposition = response.headers.get('Content-Disposition') ?? '';
        const match = /filename="?([^";\n]+)"?/.exec(contentDisposition);
        const filename = match?.[1] ?? anexo.nomeOriginal;
        if (!response.body) return;
        const url = URL.createObjectURL(response.body);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        this.messageService.add({severity: 'success', summary: 'Sucesso!', detail: 'Download realizado com sucesso.'});
      },
      error: () => this.messageService.add({
        severity: 'error',
        summary: 'Erro!',
        detail: 'Falha ao realizar o download.'
      })
    });
  }

  protected getFileObjectUrl(file: File): string {
    if (!this.objectUrlCache.has(file)) {
      this.objectUrlCache.set(file, URL.createObjectURL(file));
    }
    return this.objectUrlCache.get(file)!;
  }

  protected getFormattedFileSize(bytes: number): string {
    return StringUtils.getFormattedFileSize(bytes);
  }

  private subscribeTipoDocumentoValueChanges(): void {
    this.form.get('tipoDocumento')?.valueChanges.subscribe((value) => {
      if (!value) return;
      const descricaoArquivoCntrl = this.form.get('descricaoArquivo');
      if ((value as TipoAnexo).nome === 'Outros') {
        descricaoArquivoCntrl?.setValidators(Validators.required);
      } else {
        descricaoArquivoCntrl?.clearValidators();
        descricaoArquivoCntrl?.setValue(null);
      }
      descricaoArquivoCntrl?.updateValueAndValidity();
    });
  }

  private getTiposAnexos(): void {
    if (!this.usuarioAutenticado()) return;
    this.anexosService.getTiposAnexos().subscribe(tipos => this.tiposAnexos.set(tipos));
  }

  markAllAsTouched(): void {
    this.form.markAllAsTouched();
  }

  isValid(): boolean {
    return this.form.valid;
  }

  protected isFormControlInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control?.touched && control?.invalid);
  }

  private upload(file: File, idTipoAnexo: number): void {
    if (!this.usuarioAutenticado()) return;
    const id = ++this.anexoCounter;
    const descricaoArquivo = this.form.get('descricaoArquivo')?.value;
    this.anexosEmAndamento.update(list => [...list, {
      id,
      file,
      idTipoAnexo,
      status: 'uploading',
      descricaoOutros: descricaoArquivo
    }]);
    this.anexosService.uploadAsync(idTipoAnexo, file).subscribe({
      next: (anexo) => {
        const item = this.anexosEmAndamento().find(u => u.id === id);
        if (item?.descricaoOutros && this.tipoDocumentoSelecionado?.nome === 'Outros') {
          anexo.descricaoOutros = item.descricaoOutros;
        }
        anexo.urlLocal = this.getFileObjectUrl(file);
        this.anexosEmAndamento.update(list => list.map(u => u.id === id ? {...u, status: 'done', anexo} : u));
        this.emitAnexos();
      },
      error: (err) => {
        console.error(`[FormAnexosDemanda] Erro ao realizar upload do arquivo ${file.name}: ${err.message}`);
        this.messageService.add({severity: 'error', summary: 'Erro!', detail: 'Falha ao enviar arquivo.'});
        this.anexosEmAndamento.update(list => list.map(u => u.id === id ? {...u, status: 'error'} : u));
      }
    });
  }

  private removeItem(id: number): void {
    this.anexosEmAndamento.update(list => list.filter(u => u.id !== id));
    this.emitAnexos();
  }

  private revokeObjectUrl(file: File): void {
    if (this.objectUrlCache.has(file)) {
      URL.revokeObjectURL(this.objectUrlCache.get(file)!);
      this.objectUrlCache.delete(file);
    }
  }

  private emitAnexos(): void {
    const anexos = this.anexosEmAndamento()
      .filter(u => u.status === 'done' && u.anexo)
      .map(u => u.anexo!);
    this.anexosChange.emit(anexos);
  }
}
